import { apiClient } from '@app/lib'
import { useSugarContext } from '@app/providers'
import { type SugarUploadedDocumentsResponse } from '@app/types'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useSession } from 'next-auth/react'

export const useUploadedDocuments = ({
  enrollmentId,
  programYearId
}: {
  enrollmentId: string | null | undefined
  programYearId: string | null | undefined
}) => {
  const session = useSession()
  const { siteId } = useSugarContext()

  return useQuery({
    enabled: session.status === 'authenticated' && Boolean(siteId && enrollmentId && programYearId),
    placeholderData: keepPreviousData,
    queryKey: ['uploaded-documents', { enrollmentId, programYearId, siteId }],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<SugarUploadedDocumentsResponse>('/api/documents/uploaded', {
        params: {
          enrollmentId,
          programYearId,
          siteId
        },
        signal
      })

      // Sugar emits HTTP 200 with `success: false` for app-level failures. Surface those
      // through React Query's error state instead of letting `data.length` crash later.
      if (!data.success) {
        throw new Error(data.messages?.[0]?.message ?? 'Unable to retrieve uploaded documents at this time.')
      }

      return data
    }
  })
}
